import { ChangeEvent, FC, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { get, isEmpty } from 'lodash';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';

import SahamAccordion from 'components/SahamAccordion';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import {
  brand,
  filtredCompanies,
  getOptions,
} from 'containers/PreOpeningPage/OpposingParty/utils';
import messages from 'containers/CourtManagerLegalProceedings/messages';
import { translate, arrayTranslate } from 'containers/services/translate';
import { VEHICULE } from 'containers/data-structure';
import { displayErrorText } from 'containers/services/validateForm';

export interface VehicleCardProps {
  nbr: number;
  intl: IntlShape;
  listings: Listings;
  formValues: any;
  setFormValues: Function;
}

const filterOptions = createFilterOptions({
  matchFrom: 'start',
  stringify: getOptions,
});

const VehicleCard: FC<VehicleCardProps> = props => {
  const { listings, formValues, setFormValues, nbr } = props;
  const [expanded, setExpanded] = useState(true);
  const [formValidator] = useState<any>({});

  const handleChangeExpanded = (event: ChangeEvent<{}>, expanded: boolean) => {
    setExpanded(expanded);
  };
  const [isRegistrationNumberValid, setIsRegistrationNumberValid] = useState<
    Boolean
  >(true);
  const handleBrandChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    newValue: SagisConfig | null,
  ) =>
    setFormValues(
      Object.assign({}, { ...formValues }, { [fieldName]: newValue?.ItemKey }),
    );

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);

  const { message } = displayErrorText(
    formValidator,
    translateArrayErrorMessages(formValidator, true),
  );

  const handleFormChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
  ) => {
    if (event.target.name === VEHICULE.REGISTRATION_NUMBER) {
      const pattern = /^(?:\d{1,5}-[a-zA-Z]{1,2}-\d{1,2}|[a-zA-Z]-\d{1,9}|\d{1,2}-\d{1,6}|\d{1,5}-\d{1,2}|\d{1,5}-\d{1,2}-\d{1,2}|WW\d{1,6}|W18-\d{1,6})$/;
      setIsRegistrationNumberValid(pattern.test(event.target.value));
    }
    return setFormValues(
      Object.assign({}, { ...formValues }, { [fieldName]: event.target.value }),
    );
  };

  return (
    <SahamAccordion
      expanded={expanded}
      onChange={handleChangeExpanded}
      title={translate({ ...messages.vehicle }, props, {
        number: nbr,
      })}
    >
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Autocomplete
            id="standard-basic-xx"
            data-testid="autocomplete"
            options={get(listings, 'vehicleBrands', [])}
            getOptionLabel={getOptions}
            value={brand(listings, formValues)}
            autoSelect={true}
            onChange={handleBrandChange(VEHICULE.BRAND)}
            filterOptions={filterOptions}
            renderInput={params => (
              <TextField
                {...params}
                name={VEHICULE.BRAND}
                label={translate(
                  { ...messages.claimInformationVehicleBrand },
                  props,
                )}
                error={!!get(formValidator, VEHICULE.BRAND)}
                helperText={message[VEHICULE.BRAND]}
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label={translate({ ...messages.registration }, props)}
            onChange={handleFormChange(VEHICULE.REGISTRATION_NUMBER)}
            name={VEHICULE.REGISTRATION_NUMBER}
            value={get(formValues, [VEHICULE.REGISTRATION_NUMBER])}
            InputProps={{ inputProps: { maxlength: 20 } }}
            error={
              !!get(formValidator, VEHICULE.REGISTRATION_NUMBER) ||
              (!isRegistrationNumberValid &&
                translate(messages.registrationNumberAlert, props))
            }
            helperText={
              message[VEHICULE.REGISTRATION_NUMBER] ||
              (!isRegistrationNumberValid &&
                translate(messages.registrationNumberAlert, props))
            }
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel>
              {translate({ ...messages.opposingCompanies }, props)}
            </InputLabel>
            <Select
              onChange={handleFormChange(VEHICULE.OPPOSING_PARTY)}
              name={VEHICULE.OPPOSING_PARTY}
              value={get(formValues, [VEHICULE.OPPOSING_PARTY], '')}
            >
              {!isEmpty(get(listings, 'compagnies', [])) &&
                filtredCompanies(
                  listings!.compagnies,
                  listings!.insuranceCompanies,
                ).map(company => (
                  <MenuItem value={company.ItemKey} key={company.ItemKey}>
                    {company.ItemDescription}
                  </MenuItem>
                ))}
            </Select>
            {!!get(formValidator, VEHICULE.OPPOSING_PARTY) && (
              <FormHelperText className="error">
                {message[VEHICULE.OPPOSING_PARTY]}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label={translate({ ...messages.opposingFirstName }, props)}
            onChange={handleFormChange(VEHICULE.FIRST_NAME)}
            name={VEHICULE.FIRST_NAME}
            value={get(formValues, [VEHICULE.FIRST_NAME])}
            error={!!get(formValidator, VEHICULE.FIRST_NAME)}
            helperText={message[VEHICULE.FIRST_NAME]}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label={translate({ ...messages.opposingLastName }, props)}
            onChange={handleFormChange(VEHICULE.LAST_NAME)}
            name={VEHICULE.LAST_NAME}
            value={get(formValues, [VEHICULE.LAST_NAME])}
            error={!!get(formValidator, VEHICULE.LAST_NAME)}
            helperText={message[VEHICULE.LAST_NAME]}
          />
        </Grid>
      </Grid>
    </SahamAccordion>
  );
};

export default injectIntl(VehicleCard);
